/* lib/aiAccess.js — «нет доступа → нет модели»: чистая логика для экранов.

   Правило владельца: у неактивного пространства (access.state ∈ no_plan |
   trial_over | expired) ни один запрос не доходит до LLM. Бэкенд закрывает
   каждый AI-вход ответом 402 {detail, code:'subscription_expired', state};
   фоновая сборка дерева останавливается с `stop_reason:'subscription_expired'`
   (узлы ждут в `waiting`, после оплаты `resume` продолжает); commit черновика
   создаёт сущность, но говорит `tree_build_skipped:'subscription_expired'`;
   отчёт v2 сохраняет расчётную часть с `ai_skipped:'subscription_expired'`.

   Раньше экраны превращали это в глухое «Не удалось…» или — хуже — в зелёное
   «Добавлено в систему». Здесь ОДНО сопоставление «код → текст + куда вести»;
   экраны его не копируют.

   Инвариант (близнец planAccess.js / planLock.js): нет ключа, другой код или
   старый бэкенд → null/false, экран ведёт себя ровно как раньше. */

export const SUBSCRIPTION_EXPIRED = 'subscription_expired';

// Запасной текст, если 402 пришёл без detail (detail бэкенда всегда главнее).
export const AI_CLOSED_FALLBACK = 'AI-функции входят в платный тариф. Выберите тариф, чтобы продолжить.';

const TREE_STOP_NOTE = 'Сборка рецептов остановлена: AI-функции недоступны без активного тарифа. '
  + 'Выберите тариф и нажмите «Достроить».';
const TREE_SKIPPED_NOTE = 'Создано, но рецепты и составы не собраны: AI-функции недоступны без активного тарифа.';
const REPORT_AI_SKIPPED_NOTE = 'Диагностика AI не выполнена: доступ к модели закрыт — выберите тариф';

/* Код ошибки из любой формы, в которой он доходит до экрана:
   ошибка unwrap ({status, code, detail}), сырой ответ apiFetch ({ok,status,data}),
   тело ответа или SSE-кадр ({type:'error', code, detail}). */
function codeOf(x) {
  if (!x || typeof x !== 'object') return null;
  if (x.code) return x.code;
  if (x.data && typeof x.data === 'object' && x.data.code) return x.data.code;
  return null;
}

function detailOf(x) {
  if (!x || typeof x !== 'object') return null;
  if (typeof x.detail === 'string' && x.detail) return x.detail;
  if (x.data && typeof x.data === 'object' && typeof x.data.detail === 'string' && x.data.detail) return x.data.detail;
  return null;
}

/* Закрыт ли доступ к модели (402 subscription_expired). */
export function isAiClosed(x) {
  return codeOf(x) === SUBSCRIPTION_EXPIRED;
}

/* Текст для пользователя, если доступ закрыт; иначе null — экран оставляет свой. */
export function aiClosedDetail(x) {
  if (!isAiClosed(x)) return null;
  return detailOf(x) || AI_CLOSED_FALLBACK;
}

/* Куда ведёт «Выбрать тариф» (как в FE-1 и пузырях чата): бизнес-пространство —
   тариф бизнеса (квота и оплата общие на пространство), личное — свой тариф. */
export function tariffTarget(workspace) {
  return workspace && workspace.is_business
    ? '/settings?tab=business&biz=tariff'
    : '/settings?tab=subscription';
}

/* Причина остановки сборки дерева. Принимает и ответ бэкенда (`stop_reason`),
   и снимок провайдера чата (`stopReason`). Ручная «Стоп» (reason '') → null. */
export function treeStopNote(job) {
  if (!job || typeof job !== 'object') return null;
  const reason = job.stop_reason !== undefined ? job.stop_reason : job.stopReason;
  return reason === SUBSCRIPTION_EXPIRED ? TREE_STOP_NOTE : null;
}

/* Ответ commit черновика: сущность создана, а сборку дерева бэкенд не запускал. */
export function treeSkippedNote(res) {
  return res && res.tree_build_skipped === SUBSCRIPTION_EXPIRED ? TREE_SKIPPED_NOTE : null;
}

/* Пометка в отчёте v2: расчёт сохранён, AI-диагностика отказана доступом. */
export function reportAiSkippedNote(result) {
  return result && result.ai_skipped === SUBSCRIPTION_EXPIRED ? REPORT_AI_SKIPPED_NOTE : null;
}
